import { clsx, type WithClassName, type WithDataTestId } from "@regrello/core-utils";
import { cva } from "class-variance-authority";
import { Switch } from "radix-ui";
import React from "react";
import type { RegrelloIntent } from "../../tokens/enums/regrelloIntent";

export type RegrelloSwitchProps = WithClassName &
  WithDataTestId &
  // (clewis): Use onCheckedChange instead of onChange.
  Omit<React.ComponentProps<typeof Switch.Root>, "onChange"> & {
    /**
     * The semantic intent of the switch.
     *
     * @default "primary"
     */
    intent?: Extract<RegrelloIntent, "primary" | "special">;
  };

const RegrelloSwitch = React.memo(
  React.forwardRef<React.ElementRef<typeof Switch.Root>, Omit<RegrelloSwitchProps, "ref">>(function RegrelloSwitchFn(
    { className, dataTestId, intent = "primary", ...props },
    ref,
  ) {
    return (
      <Switch.Root
        ref={ref}
        className={clsx(switchRootVariants({ intent }), className)}
        data-testid={dataTestId}
        disabled={props.disabled}
        {...props}
      >
        <Switch.Thumb
          className={`
            block
            w-3.5
            h-3.5

            bg-background
            rounded-full

            transition-transform
            duration-100
            will-change-transform
            data-[state=checked]:translate-x-3.5
        `}
        />
      </Switch.Root>
    );
  }),
);
RegrelloSwitch.displayName = Switch.Root.displayName;

/** Styles to apply to the root element of the switch. */
const switchRootVariants = cva<{
  intent: Record<NonNullable<RegrelloSwitchProps["intent"]>, string>;
}>(
  clsx(
    // Visual
    "h-4.5",
    "min-w-8",
    "rounded-full",
    "p-0.5",

    // Layout
    "flex",
    "items-center",
    "shrink-0",
    "relative",

    // Off state
    "bg-neutral-solidMuted",
    "hover:bg-neutral-solid",
    "active:bg-neutral-solidHovered",

    // Focus state
    "focus-visible:outline-primary-solid",
    "focus-visible:outline-offset-3",

    // Disabled state
    "disabled:bg-neutral-border",
    "disabled:opacity-disabled",
    "disabled:outline-none",
    "disabled:cursor-not-allowed",

    // Pressed state (iff the switch is not disabled)
    "data-[state=checked]:shadow-none",
    "data-[state=checked]:active:not-disabled:rg-solidPressed",
  ),
  {
    variants: {
      intent: {
        primary: clsx(
          "data-[state=checked]:bg-primary-solid",
          "data-[state=checked]:text-primary-textMuted",
          "data-[state=checked]:hover:bg-primary-solidHovered",

          "disabled:data-[state=checked]:bg-primary-solidMuted",
        ),
        special: clsx(
          "data-[state=checked]:bg-special-solid",
          "data-[state=checked]:text-special-textMuted",
          "data-[state=checked]:hover:bg-special-solidHovered",

          "disabled:data-[state=checked]:bg-special-solidMuted",
        ),
      },
    },
  },
);

export { RegrelloSwitch };
